import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UpdateInboxEntryInput } from '@this+that/models';
import log from 'loglevel';

import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import { useUpdateInboxEntry } from './useUpdateInboxEntry';
import { useInboxEntriesStore, useUnreadStore } from '../../store';
import {
  clearOptimisticMove,
  recordOptimisticMove,
} from '../../utils/optimisticMoveGuard';
import { useCustomToast } from '../useCustomToast';

interface AcceptSuggestionIntoSubgroupParams {
  inboxEntryId: string;
  from: string | null;
  to: string | null;
}

export const useAcceptSuggestionIntoSubgroup = () => {
  const { t } = useTranslation();
  const { handleUpdateInboxEntry } = useUpdateInboxEntry();
  const { updateInboxEntry, updateInboxEntryFields } =
    useActiveInboxEntriesSlice();
  const { promiseToast } = useCustomToast();
  const { markAsRead } = useUnreadStore();
  const [isAccepting, setIsAccepting] = useState(false);

  const acceptSuggestionIntoSubgroup = useCallback(
    async ({
      inboxEntryId,
      from,
      to,
    }: AcceptSuggestionIntoSubgroupParams): Promise<boolean> => {
      setIsAccepting(true);
      updateInboxEntryFields(inboxEntryId, {
        isSuggestion: false,
        taskSubgroupId: to,
      });
      const acceptedEntry = useInboxEntriesStore
        .getState()
        .taskManager.activeTaskGroup.inboxEntries.find(
          (entry) => entry.id === inboxEntryId,
        );
      recordOptimisticMove(inboxEntryId, {
        taskSubgroupId: to,
        orderKey: acceptedEntry?.orderKey,
      });

      const acceptPromise = (async () => {
        try {
          const updateData: UpdateInboxEntryInput = {
            id: inboxEntryId,
            isSuggestion: false,
            taskSubgroupId: to,
          };

          const updatedEntry = await handleUpdateInboxEntry(updateData);

          if (!updatedEntry) {
            throw new Error('Failed to update inbox entry');
          }

          updateInboxEntry(updatedEntry);
          markAsRead(inboxEntryId, updatedEntry.taskGroupId);
          log.info(
            `Suggestion accepted into subgroup for inbox entry ${inboxEntryId}`,
          );
          return true;
        } catch (error) {
          updateInboxEntryFields(inboxEntryId, {
            isSuggestion: true,
            taskSubgroupId: from,
          });
          clearOptimisticMove(inboxEntryId);
          log.error('Error accepting suggestion into subgroup:', error);
          throw error;
        } finally {
          setIsAccepting(false);
        }
      })();

      const didAccept = await promiseToast(
        acceptPromise,
        t('doBox.actions.acceptingSuggestion'),
        t('doBox.actions.acceptSuggestionSuccess'),
        t('doBox.actions.acceptSuggestionError'),
      );

      return didAccept === true;
    },
    [
      handleUpdateInboxEntry,
      updateInboxEntry,
      updateInboxEntryFields,
      promiseToast,
      t,
      markAsRead,
    ],
  );

  return {
    acceptSuggestionIntoSubgroup,
    isAccepting,
  };
};
